import Tag from "components/Tag";
import { Paper } from "helpers/api";
import pluralize from "helpers/pluralize";
import useLabels from "hooks/useLabels";
import { useState } from "react";
import Citation, { CitationProps } from "./Citation";
import { CitationMetadata } from "../plugins/remark/types";

interface CitationGroupProps
  extends Omit<
    CitationProps,
    "paperIndex" | "paper" | "firstInGroup" | "onClick"
  > {
  citations: CitationMetadata[];
  isTableCitation: boolean;
  stableKey: string;
  papers?: Paper[];
  referenceLineNumber: number;
  associatedSentence: string;
  showPapercount?: boolean;
  onClickCitation?: (
    paper: Paper,
    paperIndex: number,
    referenceLineNumber: number,
    associatedSentence: string,
    quote?: string,
    quoteIndex?: number
  ) => void;
  followingPunctuation?: string;
  showCitationMeterResult?: boolean;
  fullTextPaperIds?: string[];
}

const MAX_TABLE_VISIBLE_CITATIONS = 4;
const MAX_SENTENCE_VISIBLE_CITATIONS = 8;

export const CitationGroup = ({
  citations,
  isTableCitation,
  followingPunctuation = "",
  showPapercount = false,
  fullTextPaperIds = [],
  ...props
}: CitationGroupProps) => {
  const [analysisLabels] = useLabels("pro-analysis");
  const [showAll, setShowAll] = useState(false);
  const citationCount = citations.length;
  const maxVisibleCitations = isTableCitation
    ? MAX_TABLE_VISIBLE_CITATIONS
    : MAX_SENTENCE_VISIBLE_CITATIONS;
  const moreCitationsCount = citationCount - maxVisibleCitations;
  const hasMoreButton = citationCount > maxVisibleCitations && !showAll;
  const fullTextPaperIdsSet = new Set(fullTextPaperIds);

  const citationsContent = (
    <span className="inline not-prose">
      {citations.map((citation, citationIndex) => {
        const isLast = citationIndex === citations.length - 1;
        const shouldHide = !showAll && citationIndex + 1 > maxVisibleCitations;

        let paper: Paper | undefined;
        let paperIndex: number = 0;

        const { papers, ...otherProps } = props;

        if (citation.paper_id) {
          // Find paper by paper_id (for <paper_cite> tags)
          paper = props.papers?.find((p) => p.paper_id === citation.paper_id);
          paperIndex =
            papers?.findIndex((p) => p.paper_id === citation.paper_id) ?? 0;
        } else if (citation.paperIndex !== undefined) {
          paper = papers?.[citation.paperIndex];
          paperIndex = citation.paperIndex;
        }

        const hasUsedFullText = !!(
          paper?.paper_id && fullTextPaperIdsSet.has(paper.paper_id)
        );

        // Last citation with following punctuation (and no more button)
        if (isLast && followingPunctuation && !hasMoreButton) {
          return (
            <span
              key={`${props.stableKey}-${citation.paper_id ?? citation.paperIndex}-${citation.quoteIndex}`}
              className="whitespace-nowrap"
            >
              <Citation
                paperIndex={paperIndex}
                paper={paper}
                quote={citation?.quote}
                quoteIndex={citation?.quoteIndex}
                onClick={props.onClickCitation}
                {...otherProps}
                hideCitation={shouldHide}
                lastInGroup={true}
                hasUsedFullText={hasUsedFullText}
              />
              {followingPunctuation}
            </span>
          );
        }

        // Regular citation
        return (
          <Citation
            paperIndex={paperIndex}
            key={`${props.stableKey}-${citation.paper_id ?? citation.paperIndex}-${citation.quoteIndex}`}
            paper={paper}
            quote={citation?.quote}
            quoteIndex={citation?.quoteIndex}
            onClick={props.onClickCitation}
            {...otherProps}
            hideCitation={shouldHide}
            lastInGroup={isLast && !hasMoreButton}
            hasUsedFullText={hasUsedFullText}
          />
        );
      })}
      {hasMoreButton && (
        <span className="whitespace-nowrap">
          <Tag
            label={pluralize(analysisLabels["more-citations"], {
              MORE_COUNT: moreCitationsCount,
            })}
            onClick={() => setShowAll(!showAll)}
            size="sm"
            className="bg-bg-subtle"
          />
          {followingPunctuation}
        </span>
      )}
    </span>
  );

  // For regular citations, only wrap in div if we need to show paper count
  if (showPapercount) {
    return (
      <div>
        {citationsContent}
        <div className="mt-2 tiny-normal text-fg-muted">
          {citationCount} {citationCount === 1 ? "paper" : "papers"}
        </div>
      </div>
    );
  }

  // Return just the inline content without wrapper
  return citationsContent;
};
